import { useState, useEffect } from 'react';

// Single source of truth for component layout configuration
export const LAYOUT_CONFIG = {
  // Base container settings
  container: {
    maxWidth: {
      ultrawide: '1400px',
      desktop: '1200px',
      tablet: '100%',
      mobile: '100%'
    },
    margin: '0 auto',
    padding: {
      ultrawide: '3rem 2rem 1.5rem 2rem',
      desktop: '2.5rem 1.5rem 1.25rem 1.5rem',
      tablet: '2rem 1rem 1rem 1rem',
      mobile: '1.5rem 0.75rem 0.75rem 0.75rem'
    }
  },
  
  // Component-specific overrides with consistent vertical spacing
  components: {
    hero: {
      padding: {
        ultrawide: '0',
        desktop: '0',
        tablet: '0',
        mobile: '0'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    about: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    appDemos: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    services: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    contact: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    faq: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    partners: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    },
    blog: {
      padding: {
        ultrawide: '2rem 2rem 2rem 2rem',
        desktop: '1.5rem 1.5rem 1.5rem 1.5rem',
        tablet: '1rem 1rem 1rem 1rem',
        mobile: '0.75rem 0.75rem 0.75rem 0.75rem'
      },
      marginBottom: {
        ultrawide: '4rem',
        desktop: '3rem',
        tablet: '2.5rem',
        mobile: '2rem'
      }
    }
  },
  
  // Typography settings with window-aware spacing
  typography: {
    title: {
      marginBottom: {
        ultrawide: '50px',
        desktop: '40px',
        tablet: '30px',
        mobile: '20px'
      }
    },
    paragraph: {
      marginBottom: {
        ultrawide: '30px',
        desktop: '25px',
        tablet: '20px',
        mobile: '15px'
      }
    }
  },
  
  // Grid settings with adaptive gutters
  grid: {
    gutter: {
      ultrawide: [40, 40],
      desktop: [32, 32],
      tablet: [24, 24],
      mobile: [16, 16]
    }
  },
  
  // Enhanced breakpoints for better window awareness
  breakpoints: {
    mobile: 768,
    tablet: 1024,
    desktop: 1200,
    ultrawide: 1600
  },
  
  // Column configurations for different screen sizes
  columns: {
    ultrawide: {
      about: { xs: 24, sm: 12, md: 8, lg: 6, xl: 6 },
      // DWEB-20: four offers, not three — an 8-span row leaves a lone orphan
      // card, so services go 2x2 from tablet up.
      services: { xs: 24, sm: 12, md: 12, lg: 12, xl: 12 },
      appDemos: { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 },
      blog: { xs: 24, sm: 12, md: 8, lg: 6, xl: 6 }
    },
    desktop: {
      about: { xs: 24, sm: 12, md: 8, lg: 8, xl: 8 },
      services: { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 },
      appDemos: { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 },
      blog: { xs: 24, sm: 12, md: 8, lg: 6, xl: 6 }
    },
    tablet: {
      about: { xs: 24, sm: 12, md: 12, lg: 12 },
      services: { xs: 24, sm: 24, md: 12, lg: 12 },
      appDemos: { xs: 24, sm: 24, md: 24, lg: 24 },
      blog: { xs: 24, sm: 12, md: 12, lg: 12 }
    },
    mobile: {
      about: { xs: 24 },
      services: { xs: 24 },
      appDemos: { xs: 24 },
      blog: { xs: 24 }
    }
  }
};

// Utility function to get responsive padding
export const getResponsivePadding = (componentName = 'default') => {
  const config = LAYOUT_CONFIG.components[componentName] || LAYOUT_CONFIG.container;
  
  return `
    padding: ${config.padding.mobile};
    
    @media (min-width: ${LAYOUT_CONFIG.breakpoints.mobile + 1}px) {
      padding: ${config.padding.tablet};
    }
    
    @media (min-width: ${LAYOUT_CONFIG.breakpoints.desktop}px) {
      padding: ${config.padding.desktop};
    }
  `;
};

// Utility function to get responsive margins
export const getResponsiveMargin = (type = 'title') => {
  const config = LAYOUT_CONFIG.typography[type];
  
  return `
    margin-bottom: ${config.marginBottom.mobile};
    
    @media (min-width: ${LAYOUT_CONFIG.breakpoints.mobile + 1}px) {
      margin-bottom: ${config.marginBottom.tablet};
    }
    
    @media (min-width: ${LAYOUT_CONFIG.breakpoints.desktop}px) {
      margin-bottom: ${config.marginBottom.desktop};
    }
  `;
};

// Utility function to get responsive grid gutter
export const getResponsiveGutter = () => {
  const isMobile = window.innerWidth <= LAYOUT_CONFIG.breakpoints.mobile;
  const isTablet = window.innerWidth <= LAYOUT_CONFIG.breakpoints.desktop;
  
  if (isMobile) return LAYOUT_CONFIG.grid.gutter.mobile;
  if (isTablet) return LAYOUT_CONFIG.grid.gutter.tablet;
  return LAYOUT_CONFIG.grid.gutter.desktop;
};

// Enhanced hook for window-aware responsive layout
export const useResponsiveLayout = (componentName) => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Enhanced breakpoint detection
  const isMobile = windowSize.width <= LAYOUT_CONFIG.breakpoints.mobile;
  const isTablet = windowSize.width > LAYOUT_CONFIG.breakpoints.mobile && windowSize.width <= LAYOUT_CONFIG.breakpoints.tablet;
  const isDesktop = windowSize.width > LAYOUT_CONFIG.breakpoints.tablet && windowSize.width <= LAYOUT_CONFIG.breakpoints.ultrawide;
  const isUltrawide = windowSize.width > LAYOUT_CONFIG.breakpoints.ultrawide;
  
  // Determine current screen category
  let screenCategory = 'mobile';
  if (isTablet) screenCategory = 'tablet';
  else if (isDesktop) screenCategory = 'desktop';
  else if (isUltrawide) screenCategory = 'ultrawide';
  
  const config = LAYOUT_CONFIG.components[componentName] || LAYOUT_CONFIG.container;
  
  // Get appropriate padding based on screen size
  let currentPadding = config.padding.mobile;
  if (isTablet) currentPadding = config.padding.tablet;
  else if (isDesktop) currentPadding = config.padding.desktop;
  else if (isUltrawide) currentPadding = config.padding.ultrawide;
  
  // Get appropriate margin bottom for consistent spacing
  let currentMarginBottom = config.marginBottom?.mobile || '2rem';
  if (isTablet) currentMarginBottom = config.marginBottom?.tablet || '2.5rem';
  else if (isDesktop) currentMarginBottom = config.marginBottom?.desktop || '3rem';
  else if (isUltrawide) currentMarginBottom = config.marginBottom?.ultrawide || '4rem';
  
  // Get appropriate container max width
  let maxWidth = LAYOUT_CONFIG.container.maxWidth.mobile;
  if (isTablet) maxWidth = LAYOUT_CONFIG.container.maxWidth.tablet;
  else if (isDesktop) maxWidth = LAYOUT_CONFIG.container.maxWidth.desktop;
  else if (isUltrawide) maxWidth = LAYOUT_CONFIG.container.maxWidth.ultrawide;
  
  // Get appropriate gutter
  let currentGutter = LAYOUT_CONFIG.grid.gutter.mobile;
  if (isTablet) currentGutter = LAYOUT_CONFIG.grid.gutter.tablet;
  else if (isDesktop) currentGutter = LAYOUT_CONFIG.grid.gutter.desktop;
  else if (isUltrawide) currentGutter = LAYOUT_CONFIG.grid.gutter.ultrawide;
  
  // Get appropriate typography margins
  let titleMargin = LAYOUT_CONFIG.typography.title.marginBottom.mobile;
  let paragraphMargin = LAYOUT_CONFIG.typography.paragraph.marginBottom.mobile;
  
  if (isTablet) {
    titleMargin = LAYOUT_CONFIG.typography.title.marginBottom.tablet;
    paragraphMargin = LAYOUT_CONFIG.typography.paragraph.marginBottom.tablet;
  } else if (isDesktop) {
    titleMargin = LAYOUT_CONFIG.typography.title.marginBottom.desktop;
    paragraphMargin = LAYOUT_CONFIG.typography.paragraph.marginBottom.desktop;
  } else if (isUltrawide) {
    titleMargin = LAYOUT_CONFIG.typography.title.marginBottom.ultrawide;
    paragraphMargin = LAYOUT_CONFIG.typography.paragraph.marginBottom.ultrawide;
  }
  
  // Get column configuration for the component
  const columnConfig = LAYOUT_CONFIG.columns[screenCategory]?.[componentName] || 
                      LAYOUT_CONFIG.columns.desktop[componentName] || 
                      { xs: 24, sm: 12, md: 8 };
  
  return {
    windowSize,
    isMobile,
    isTablet,
    isDesktop,
    isUltrawide,
    screenCategory,
    padding: currentPadding,
    marginBottom: currentMarginBottom,
    maxWidth,
    gutter: currentGutter,
    titleMargin,
    paragraphMargin,
    columnConfig,
    // Utility functions for component-specific layouts
    getOptimalColumns: (itemCount) => {
      if (isUltrawide && itemCount >= 4) return 6; // 4 columns on ultrawide
      if (isDesktop && itemCount >= 3) return 8;   // 3 columns on desktop
      if (isTablet && itemCount >= 2) return 12;   // 2 columns on tablet
      return 24; // 1 column on mobile or when few items
    },
    // Density-aware spacing
    getDensitySpacing: (density = 'normal') => {
      const multiplier = density === 'compact' ? 0.75 : density === 'comfortable' ? 1.25 : 1;
      return {
        padding: currentPadding.split(' ').map(p => `${parseFloat(p) * multiplier}${p.replace(/[\d.]/g, '')}`).join(' '),
        titleMargin: `${parseFloat(titleMargin) * multiplier}px`,
        paragraphMargin: `${parseFloat(paragraphMargin) * multiplier}px`
      };
    }
  };
};
